import { useEffect, useState } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useGlobalStore } from '../../application/store/store.tsx';
import { useNavigate } from 'react-router-dom';
import { NavigationRoutes } from '../../application/router/routes.ts';
import AvailableStorages from './components/AvailableStorages.tsx';
import { Alert, Box, Button, Step, StepLabel, Stepper } from '@mui/material';
import Sign from './components/Sign.tsx';
import {
    TransactionOverview,
    TransactionType,
} from '../../application/types/transactionType.ts';
import SignedSuccess from './components/SignedSuccess.tsx';
import DrugsInStorage from './components/DrugsInStorage.tsx';
import { StorageLocation } from '../../application/types/storageLocation.ts';
import { StorageType } from '../../application/types/storageType.ts';
import { getTransactionAlert } from '../../common/utils/transactionValidation.tsx';

const steps = ['Lagerställe', 'Läkemedel'];

const ShiftChangeInventory = () => {
    const [isStepperOpen, setIsStepperOpen] = useState(false);
    const toggleStepper = () => {
        setIsStepperOpen(!isStepperOpen);
    };

    const resetStorage = useGlobalStore((state) => state.resetAvailableStorage);
    const resetSelectedDrug = useGlobalStore(
        (state) => state.resetSelectedDrug
    );
    const { setDoubleSignature } = useGlobalStore();

    const shiftChangeInventoryOverview = useGlobalStore(
        (state) => state.shiftChangeInventoryOverview
    );
    const selectedEmergency = useGlobalStore(
        (state) => state.selectedEmergency
    );
    const setSelectedEmergency = useGlobalStore(
        (state) => state.setSelectedEmergency
    );
    const { resetDrugQuantities } = useGlobalStore(
        (state) => state.drugQuantitiesView
    );

    const stepContents = [`${selectedEmergency?.name ?? ''}`, '', ''];

    const [activeStep, setActiveStep] = useState(0);
    const [searchTermStorage, setSearchTermStorage] = useState<string>('');

    const navigate = useNavigate();

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleNavigateBack = () => {
        // Clear all state before navigating
        resetSelectedDrug();
        resetStorage();

        // Reset overview state
        useGlobalStore.getState().resetOverviews();

        navigate(NavigationRoutes.transaction);
    };

    const handleSelectEmergency = (emergency) => {
        setSelectedEmergency(emergency);
    };

    useEffect(() => {
        return () => {
            resetSelectedDrug();
            resetStorage();
            setDoubleSignature(false);
            resetDrugQuantities();
        };
    }, [resetSelectedDrug, resetStorage, setDoubleSignature]);
    return (
        <div>
            <div className="flex flex-row justify-between items-center mb-4 text-xs sm:text-base">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleNavigateBack}
                    color="primary"
                    className="!text-xs sm:!text-base"
                    size="small"
                    sx={{
                        '@media (min-width: 640px)': {
                            fontSize: 'inherit',
                            padding: '6px 16px',
                        },
                    }}
                >
                    Tillbaka
                </Button>
            </div>
            {(shiftChangeInventoryOverview ===
                TransactionOverview.formOverview ||
                shiftChangeInventoryOverview ===
                    TransactionOverview.doubleSignOverview) && (
                <div className="flex min-h-screen bg-gray-50 relative">
                    {/* Stepper Toggle Button - Only visible below 1024px */}
                    <div className="hidden max-lg:block">
                        <button
                            onClick={toggleStepper}
                            className={`fixed top-1/2 -translate-y-1/2 z-50 bg-gray-200 p-2 rounded-r-md shadow-md transition-transform duration-300 ${
                                isStepperOpen
                                    ? 'translate-x-64'
                                    : 'translate-x-0'
                            }`}
                            style={{ left: 0 }}
                        >
                            {isStepperOpen ? (
                                <ChevronLeftIcon />
                            ) : (
                                <ChevronRightIcon />
                            )}
                        </button>
                    </div>

                    {/* Stepper Container */}
                    <div
                        className={`
                            lg:w-1/5 lg:block lg:relative lg:translate-x-0
                            max-lg:fixed max-lg:left-0 max-lg:top-0 max-lg:h-full max-lg:w-64 max-lg:z-40
                            transition-transform duration-300
                            p-5 shadow-xl rounded-lg bg-white
                            ${!isStepperOpen && 'max-lg:-translate-x-full'}
                        `}
                    >
                        <Stepper activeStep={activeStep} orientation="vertical">
                            {steps.map((label, index) => (
                                <Step key={index}>
                                    <StepLabel>
                                        <div className="flex flex-col">
                                            <span className="text-lg font-semibold text-gray-900 text-left">
                                                {label}
                                            </span>
                                            <span className="text-sm text-gray-600 italic text-left">
                                                {stepContents[index]}
                                            </span>
                                        </div>
                                    </StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                    </div>

                    {/* Overlay - Only visible below 1024px when stepper is open */}
                    {isStepperOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-30 hidden max-lg:block"
                            onClick={toggleStepper}
                        />
                    )}

                    {/* Main Content */}
                    <div className="w-full lg:w-4/5 p-2 sm:p-5 shadow-xl rounded-lg lg:ml-5 mx-2 sm:mx-5 overflow-auto">
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            mb={4}
                        >
                            <Button
                                disabled={
                                    shiftChangeInventoryOverview ===
                                        TransactionOverview.doubleSignOverview ||
                                    activeStep === 0
                                }
                                onClick={handleBack}
                                variant="text"
                                startIcon={<ArrowBackIcon />}
                                sx={{
                                    padding: { xs: '4px 8px', sm: '6px 16px' },
                                }}
                            >
                                Tillbaka
                            </Button>
                            <h1 className="text-center text-lg sm:text-2xl">
                                Registrera skiftbyte
                            </h1>
                            <Button
                                onClick={handleNext}
                                disabled={activeStep === steps.length - 1}
                                variant="text"
                                endIcon={
                                    activeStep !== steps.length - 1 ? (
                                        <ArrowForwardIcon />
                                    ) : (
                                        ''
                                    )
                                }
                            >
                                {activeStep === steps.length - 1 ? '' : 'Nästa'}
                            </Button>
                        </Box>

                        {/* Step Contents */}
                        {activeStep === 0 && (
                            <div className="flex flex-col items-center">
                                <Box
                                    sx={{
                                        width: {
                                            sm: '20rem',
                                            xs: '20rem',
                                            lg: '24rem',
                                        },
                                        maxWidth: '100%',
                                        mx: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    <AvailableStorages
                                        label="Akutväska"
                                        searchTerm={searchTermStorage}
                                        setSearchTerm={setSearchTermStorage}
                                        handleSelectEmergency={
                                            handleSelectEmergency
                                        }
                                        storageType={StorageType.ambulance}
                                        transactionType={
                                            TransactionType.ShiftCrosscheck
                                        }
                                        handleSelect={function (
                                            storage: StorageLocation
                                        ): void {
                                            throw new Error(
                                                `Invalid storage type: ${storage}`
                                            );
                                        }}
                                    />

                                    <Alert
                                        className="mt-6 max-w-2xl"
                                        severity="info"
                                    >
                                        <ul>
                                            <li>
                                                <strong>
                                                    Inventering vid skiftbyte
                                                </strong>
                                            </li>
                                            <li>1) Välj akutväska</li>
                                            <li>
                                                2) I nästa steg får du de
                                                läkemedel och antalet som
                                                förväntas finns i akutväska
                                            </li>
                                            <li>
                                                3) Du kan ändra antalet om det
                                                inte stämmer
                                            </li>
                                            <li>4) Signera inventeringen</li>
                                            <li>
                                                5) Vid behov be en kollega
                                                dubbelsignera
                                            </li>
                                        </ul>
                                    </Alert>
                                </Box>
                            </div>
                        )}
                        {activeStep === 1 && (
                            <div>
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    width="100%"
                                    mb={2}
                                >
                                    {getTransactionAlert(
                                        TransactionType.ShiftCrosscheck,
                                        undefined,
                                        undefined,
                                        undefined,
                                        undefined,
                                        selectedEmergency,
                                        undefined
                                    )}
                                </Box>
                                {selectedEmergency && (
                                    <div className="flex flex-col items-center">
                                        <Box
                                            sx={{
                                                width: {
                                                    sm: '20rem',
                                                    xs: '20rem',
                                                    lg: '24rem',
                                                },
                                                maxWidth: '100%',
                                                mx: 'auto',
                                                mb: 2,
                                            }}
                                        >
                                            <DrugsInStorage
                                                storageId={selectedEmergency.id}
                                            />
                                        </Box>
                                    </div>
                                )}
                                <Sign
                                    transactionType={
                                        TransactionType.ShiftCrosscheck
                                    }
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}
            {shiftChangeInventoryOverview ===
                TransactionOverview.successOverview && (
                <SignedSuccess
                    transactionFormOverview={TransactionType.ShiftCrosscheck}
                />
            )}
        </div>
    );
};

export default ShiftChangeInventory;
